'use client';

import type { GroupBase, MultiValue, OptionsOrGroups } from 'react-select';
import type { UpdatingDataCriteria, UpdatingDataValue } from '~/components/Profile/model';
import type { CriteriaChoiceType } from '~/model/CriteriaChoiceType';
import type { CriteriaType } from '~/model/CriteriaType';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import { ClearIndicator } from '@/app/[locale]/projects/components/ProjectsFiltersSports/ClearIndicator';
import { DropdownIndicator } from '@/app/[locale]/projects/components/ProjectsFiltersSports/DropdownIndicator';
import { Loader } from '@/app/components/Loader';
import { useCriterion } from '@/app/hooks/useCriterion';
import { retrieveTranslatedLabel } from '~/components/Interaction/Form/utils';
import { apiTypes } from '~/const/api';
import { getProfileSelectStyles } from '~/const/layout';
import { AcceptLanguage } from '~/const/locale';
import { IconOption } from './IconOption';

export const CriteriaSelect = ({
  criteria,
  userChoices,
  updatingUserValue,
  handleChange,
  sportId,
  required,
  helperText,
  noLabel = false,
}: {
  criteria: CriteriaType;
  userChoices: CriteriaChoiceType[];
  updatingUserValue: UpdatingDataValue;
  handleChange: (obj: UpdatingDataCriteria) => void;
  sportId?: number;
  required?: boolean;
  helperText?: string;
  noLabel?: boolean;
}) => {
  const locale = useLocale();
  const tProfile = useTranslations('PROFILE');
  const tGlobal = useTranslations('GLOBAL');

  const { criterionWithChoices, getCriterionChoices } = useCriterion({
    criterion: criteria,
  });

  const [isLoadingChoices, setIsLoadingChoices] = useState(false);
  const [isErrorDisplayed, setIsErrorDisplayed] = useState(false);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);

  const type = criterionWithChoices?.type;
  const isMulti = type === apiTypes.CHECK;
  const criteriaUri = criterionWithChoices?.['@id'];
  const criteriaChoices = criterionWithChoices?.choices || userChoices;
  const criteriaChoicesOptions
    = criteriaChoices?.map<CriteriaChoicesOption>(c => ({
      label: retrieveTranslatedLabel(
        c,
        AcceptLanguage[locale],
      ),
      value: c['@id'],
      type,
    })) || [];

  const hasBeenCleared = updatingUserValue === null;

  const handleLoadOptions = async () => {
    if (!criterionWithChoices?.choices) {
      setIsLoadingChoices(true);
      await getCriterionChoices();

      setIsLoadingChoices(false);
    }
  };

  const handleChangeValue = (val: CriteriaChoicesOption | MultiValue<CriteriaChoicesOption>) => {
    // if value come from a single select, convert it to a single entry array
    const valueArray = Array.isArray(val)
      ? val.map(v => v.value)
      : [(val as CriteriaChoicesOption)?.value];

    // Value to send
    const value
      = val == null || (val as CriteriaChoicesOption[])?.length === 0
        ? null
        : { choices: valueArray };

    return handleChange({
      value,
      criteriaUri,
      type,
      ignore: !userChoices && value === null,
    });
  };

  const findIndex
    = updatingUserValue?.choices
      && criteriaChoicesOptions.findIndex(
        c => c.value === updatingUserValue.choices?.[0],
      );

  // format single value from updatingUserValue
  const singleUpdatingValue = !isMulti
    && updatingUserValue?.choices && {
    label: criteriaChoicesOptions[findIndex]?.label,
    value: updatingUserValue.choices?.[0],
    type: criteriaChoicesOptions[findIndex]?.type,
  };

  // format multi value from updatingUserValue
  const multipleUpdatingValue
    = isMulti
      && !!updatingUserValue?.choices
      && Object.values(updatingUserValue.choices).map((choiceUri) => {
        const matchingIndex = criteriaChoicesOptions.findIndex(
          option => option.value === choiceUri,
        );
        return (
          matchingIndex > -1 && {
            label: criteriaChoicesOptions[matchingIndex]?.label,
            value: criteriaChoicesOptions[matchingIndex].value,
            type: criteriaChoicesOptions[matchingIndex]?.type,
          }
        );
      });

  const singleUserValue = !updatingUserValue
    && userChoices?.length && {
    label: retrieveTranslatedLabel(
      userChoices[0],
      AcceptLanguage[locale],
    ),
    value: userChoices[0]['@id'],
    type,
  };

  const multipleUserValue
    = !updatingUserValue
      && userChoices?.map((uc) => {
        return {
          label: retrieveTranslatedLabel(
            uc,
            AcceptLanguage[locale],
          ),
          value: uc['@id'],
          type,
        };
      });

  // format select value
  const selectValue = (() => {
    if (hasBeenCleared)
      return null;
    else if (updatingUserValue)
      return isMulti ? multipleUpdatingValue : singleUpdatingValue;
    else return isMulti ? multipleUserValue : singleUserValue;
  })();

  const isOnError = required && helperText && !selectValue;

  const selectStyles = getProfileSelectStyles();

  useEffect(() => {
    if (hasBeenFocused) {
      if (isOnError && !isErrorDisplayed)
        setIsErrorDisplayed(true);
      else if (!isOnError && isErrorDisplayed)
        setIsErrorDisplayed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnError, hasBeenFocused]);

  const loadingMessage = () => (
    <>
      <Loader />
      {tGlobal('LOADING')}
    </>
  );

  return (
    <div className="profile__field" data-testid="criteria-select">
      {!noLabel && (
        <div className="vtmn-text-input_label">
          <label>{retrieveTranslatedLabel(criteria, AcceptLanguage[locale])}</label>
        </div>
      )}
      <Select
        inputId={`criteria-${sportId || ''}${criteria?.id}`}
        name={`criteria-${sportId || ''}${criteria?.id}`}
        {...isMulti && { isMulti }}
        data-cy="selectCriteria"
        closeMenuOnSelect={!isMulti}
        blurInputOnSelect={!isMulti}
        hideSelectedOptions={false}
        styles={selectStyles}
        isClearable
        options={criteriaChoicesOptions as OptionsOrGroups<CriteriaChoicesOption, GroupBase<CriteriaChoicesOption>>}
        value={selectValue}
        aria-describedby="helper"
        aria-label={tProfile('GLOBAL.CHOOSE')}
        placeholder={tProfile('GLOBAL.CHOOSE')}
        onFocus={handleLoadOptions}
        onBlur={() => setHasBeenFocused(true)}
        isLoading={isLoadingChoices}
        loadingMessage={loadingMessage}
        noOptionsMessage={() => tGlobal('NO_OPTION')}
        onChange={handleChangeValue}
        components={{
          Option: IconOption,
          DropdownIndicator,
          ClearIndicator: ClearIndicator<CriteriaChoicesOption>,
        }}
      />
      {isErrorDisplayed && (
        <p
          id="helper"
          className="vtmn-text-input_helper-text vtmn-text-input_helper-text--error"
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

export interface CriteriaChoicesOption {
  label: string;
  value: string;
  type?: string;
}
